import type { editor } from 'monaco-editor';

type GetMonacoConfigProps = {
  disabled: boolean;
  size: string;
  placeholder?: string;
} & editor.IStandaloneEditorConstructionOptions;

export const getMonacoConfig = ({
  disabled,
  size,
  placeholder,
  ...overrides
}: GetMonacoConfigProps): editor.IStandaloneEditorConstructionOptions => ({
  wordWrap: 'on',
  minimap: { enabled: false },
  scrollbar: {
    vertical: 'hidden',
    horizontal: 'hidden',
    verticalScrollbarSize: 0,
    horizontalScrollbarSize: 0,
  },
  lineNumbers: 'off',
  glyphMargin: false,
  folding: false,
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 0,
  renderLineHighlight: 'none',
  overviewRulerLanes: 0,
  overviewRulerBorder: false,
  hideCursorInOverviewRuler: true,
  scrollBeyondLastLine: false,
  readOnly: disabled,
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontWeight: '400',
  fontSize: 14,
  lineHeight: 16, // 1rem = 16px
  suggestLineHeight: 24, // Add vertical spacing between suggestion items
  padding: { top: 0, bottom: 0 },
  automaticLayout: true,
  selectOnLineNumbers: false,
  roundedSelection: false,
  cursorStyle: 'line',
  cursorWidth: 1,
  cursorBlinking: 'blink',
  renderWhitespace: 'none',
  renderControlCharacters: false,
  fontLigatures: false,
  disableLayerHinting: true,
  placeholder: placeholder,
  rulers: [],
  renderValidationDecorations: 'off',
  showFoldingControls: 'never',
  matchBrackets: 'never',
  occurrencesHighlight: 'off',
  selectionHighlight: false,
  codeLens: false,
  contextmenu: false,
  hover: { enabled: false },
  parameterHints: { enabled: false },
  suggestOnTriggerCharacters: true,
  acceptSuggestionOnCommitCharacter: true,
  acceptSuggestionOnEnter: 'on',
  quickSuggestions: {
    other: false,
    comments: false,
    strings: false,
  },
  suggest: {
    showWords: false,
    showSnippets: false,
    showVariables: true,
  },
  'semanticHighlighting.enabled': false,
  ...overrides,
});

// Editor options
export const getVariantSpecificMonacoConfig = ({
  variant,
  ...props
}: GetMonacoConfigProps & { variant: 'textarea' | 'input' }) => {
  const baseConfig = getMonacoConfig(props);

  if (variant === 'input') {
    // For input variant, calculate padding to vertically center the text
    const inputHeight = props.size === 'compact' ? 32 : 40;
    const lineHeight = 18;
    const verticalPadding = Math.floor((inputHeight - lineHeight) / 2);

    return {
      ...baseConfig,
      lineNumbers: 'off' as const,
      fixedOverflowWidgets: true,
      acceptSuggestionOnEnter: 'on' as const,
      glyphMargin: false,
      folding: false,
      lineHeight,
      lineDecorationsWidth: 0,
      lineNumbersMinChars: 0,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'off' as const,
      scrollbar: { vertical: 'hidden' as const, horizontal: 'hidden' as const },
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      renderLineHighlight: 'none' as const,
      padding: { top: verticalPadding, bottom: verticalPadding },
    };
  }

  // For textarea variant, add some vertical padding (caller can override via props.padding)
  return {
    ...baseConfig,
    fixedOverflowWidgets: true,
    padding: props.padding ?? { top: 10, bottom: 10 },
  };
};
